/**
 * The main view: the node graph, with a readout of the regime's position beneath it.
 *
 * The graph carries the argument; the readout carries the numbers you would want if you
 * were arguing with it. Sector-by-sector detail lives in the setup panel, where it is
 * editable — duplicating it here as a second read-only list would be noise.
 */

import type { DaySnapshot, RunLog } from 'walkout-engine';
import { NodeGraph } from './NodeGraph.tsx';
import './Stage.css';

function pct(value: number): string {
  const percent = value * 100;
  if (percent >= 10) return `${Math.round(percent)}%`;
  if (percent >= 1) return `${percent.toFixed(1)}%`;
  return `${percent.toFixed(2)}%`;
}

const ACTION_COPY: Record<DaySnapshot['regime']['action'], string> = {
  repress: 'Cracking down',
  concede: 'Giving ground',
  propagandize: 'Working the media',
  coopt: 'Buying people off',
  wait: 'Waiting it out',
};

export function Stage({ run, day }: { run: RunLog; day: DaySnapshot }) {
  const worstOff = [...day.sectors].sort((a, b) => b.spent - a.spent)[0];
  const worstOffName = worstOff
    ? (run.config.sectors.find((s) => s.id === worstOff.id)?.name ?? worstOff.id)
    : null;

  return (
    <div className="stage">
      <div className="stage__graph">
        <NodeGraph run={run} day={day} />
      </div>

      <dl className="readout">
        <div className="readout__item readout__item--wide">
          <dt className="label">The regime is</dt>
          <dd>{ACTION_COPY[day.regime.action]}</dd>
        </div>
        <div className="readout__item">
          <dt className="label">Conceded</dt>
          <dd className="numeral">{pct(day.regime.concession)}</dd>
        </div>
        <div className="readout__item">
          <dt className="label">Repression</dt>
          <dd className="numeral">{pct(day.regime.repression)}</dd>
        </div>
        <div className="readout__item">
          <dt className="label">Legitimacy</dt>
          <dd className="numeral">{pct(day.regime.legitimacy)}</dd>
        </div>
        <div className="readout__item">
          <dt className="label">Nonviolent discipline</dt>
          <dd className={`numeral${day.discipline < 0.6 ? ' readout--alarm' : ''}`}>
            {pct(day.discipline)}
          </dd>
        </div>
        <div className="readout__item">
          <dt className="label">Shortages</dt>
          <dd className={`numeral${day.economy.shortage > 0.3 ? ' readout--alarm' : ''}`}>
            {pct(day.economy.shortage)}
          </dd>
        </div>
        {worstOff && worstOff.spent > 0.1 && (
          <div className="readout__item readout__item--wide">
            <dt className="label">Running out of money</dt>
            <dd className="readout__small">
              {worstOffName} — {pct(worstOff.spent)} can no longer afford to stay out
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}
